'use client'

import * as React from 'react'
import { cn } from '@/lib/utils'
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react'

/**
 * Borderless propagates from the root Table down through compound subcomponents
 * (Header, Row) so callers don't have to thread the prop manually. When
 * `borderless` is true:
 *  - Outer wrapper drops the rounded surface card (no border, no background)
 *  - Header drops its tinted background
 *  - Rows keep the bottom divider (between-row separators)
 *  - Last row's `border-b-0` rule (existing) keeps the table's bottom edge clean
 *
 * Use this when embedding the table inside another card that already provides
 * the chrome.
 */
const BorderlessContext = React.createContext(false)

export interface TableProps extends React.HTMLAttributes<HTMLTableElement> {
  borderless?: boolean
  /**
   * Additional classes for the scroll-wrapping div around the table. Useful
   * for capping height (`max-h-[...]`) when the wrapper should become the
   * scroll context for sticky table headers.
   */
  wrapperClassName?: string
}

const Table = React.forwardRef<HTMLTableElement, TableProps>(
  ({ className, borderless = false, wrapperClassName, ...props }, ref) => (
    <BorderlessContext.Provider value={borderless}>
      <div
        className={cn(
          'w-full overflow-auto',
          !borderless &&
            'rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)]',
          wrapperClassName,
        )}
      >
        <table
          ref={ref}
          className={cn('w-full caption-bottom text-sm', className)}
          {...props}
        />
      </div>
    </BorderlessContext.Provider>
  )
)
Table.displayName = 'Table'

const TableHeader = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => {
    const borderless = React.useContext(BorderlessContext)
    return (
      <thead
        ref={ref}
        className={cn(!borderless && 'bg-[var(--color-surface-hover)]', className)}
        {...props}
      />
    )
  }
)
TableHeader.displayName = 'TableHeader'

const TableBody = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <tbody
      ref={ref}
      className={cn('[&_tr:last-child]:border-0', className)}
      {...props}
    />
  )
)
TableBody.displayName = 'TableBody'

interface TableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  hoverable?: boolean
}

const TableRow = React.forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, hoverable = true, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(
        'border-b border-[var(--color-border)] transition-colors',
        hoverable && 'hover:bg-[var(--color-surface-hover)]',
        className
      )}
      {...props}
    />
  )
)
TableRow.displayName = 'TableRow'

interface TableHeadProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  sortable?: boolean
  sortDirection?: 'asc' | 'desc' | null
  onSort?: () => void
  align?: 'left' | 'center' | 'right'
}

const TableHead = React.forwardRef<HTMLTableCellElement, TableHeadProps>(
  ({ className, sortable, sortDirection, onSort, align = 'left', children, ...props }, ref) => {
    const alignClass = {
      left: 'text-left',
      center: 'text-center',
      right: 'text-right',
    }

    return (
      <th
        ref={ref}
        className={cn(
          'h-12 px-4 font-medium text-xs uppercase tracking-wider text-muted-foreground',
          alignClass[align],
          sortable && 'cursor-pointer select-none hover:text-foreground',
          className
        )}
        onClick={sortable ? onSort : undefined}
        {...props}
      >
        <div className={cn('flex items-center gap-1', align === 'center' && 'justify-center', align === 'right' && 'justify-end')}>
          {children}
          {sortable && (
            <span className="inline-flex">
              {sortDirection === 'asc' ? (
                <ChevronUp className="h-3.5 w-3.5" />
              ) : sortDirection === 'desc' ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronsUpDown className="h-3.5 w-3.5 opacity-50" />
              )}
            </span>
          )}
        </div>
      </th>
    )
  }
)
TableHead.displayName = 'TableHead'

const TableCell = React.forwardRef<HTMLTableCellElement, React.TdHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <td
      ref={ref}
      className={cn('px-4 py-3 align-middle text-sm', className)}
      {...props}
    />
  )
)
TableCell.displayName = 'TableCell'

interface TableEmptyProps extends React.HTMLAttributes<HTMLTableRowElement> {
  colSpan: number
  message?: string
}

const TableEmpty = React.forwardRef<HTMLTableRowElement, TableEmptyProps>(
  ({ colSpan, message = 'No data available', className, ...props }, ref) => (
    <tr ref={ref} className={className} {...props}>
      <td colSpan={colSpan} className="h-24 text-center text-muted-foreground">
        {message}
      </td>
    </tr>
  )
)
TableEmpty.displayName = 'TableEmpty'

const TableCompound = Object.assign(Table, {
  Header: TableHeader,
  Body: TableBody,
  Row: TableRow,
  Head: TableHead,
  Cell: TableCell,
  Empty: TableEmpty,
})

export { TableCompound as Table }
